import { useId } from "react";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { Button } from "../ui/button";
import { useLanguage } from "../../i18n/language-provider";
import { en } from "../../i18n/en";
import { ActiveConnectionChangedError } from "../../services/apiClient";

type ActiveConnectionChangedKey = keyof typeof en.activeConnectionChanged;

/**
 * The refusal's own words, read from `useLanguage()` by the notices and by the
 * screens that also toast it (the credential card, the wizard's PMS step, the
 * strategy page, the Prices page and, through `useActiveConnectionChanged`, the
 * Settings page's other cards), so none can word it differently. Defaulted
 * field by field against `en`, because a partial `t` is what the test harnesses
 * hand components here.
 */
export function activeConnectionChangedCopy(t: {
    activeConnectionChanged?: Partial<Record<ActiveConnectionChangedKey, string>>;
}): Record<ActiveConnectionChangedKey, string> {
    const group = t.activeConnectionChanged ?? {};
    const defaults: Record<ActiveConnectionChangedKey, string> = en.activeConnectionChanged;
    return Object.fromEntries(
        (Object.keys(defaults) as ActiveConnectionChangedKey[]).map((key) => [key, group[key] ?? defaults[key]]),
    ) as Record<ActiveConnectionChangedKey, string>;
}

/**
 * The id the refusal toasts of one moment share. A click that several changes
 * ride on — a wizard step that saves its room kinds and then itself, a comp set
 * edit beside a pin — is refused once per change, and says it once rather than
 * stacking the same words.
 */
export const ACTIVE_CONNECTION_CHANGED_TOAST_ID = "active-connection-changed";

/**
 * Toast a change refused because the page was loaded for another property than
 * the one open now, in the refusal's own words — and say whether it was that
 * refusal, so the caller keeps its own message for every other failure. The
 * shell has already been asked which property is open (`loadedConnection.ts`),
 * and puts its notice in place of the page when it is another one. `send` is
 * for the upload to the PMS and a request to Optivo, and `recalculate` for the
 * price run a strategy save starts, where "saved" would be the wrong word.
 */
export function toastActiveConnectionChanged(
    error: unknown,
    t: Parameters<typeof activeConnectionChangedCopy>[0],
    options: { id?: string | number; send?: boolean; recalculate?: boolean } = {},
): boolean {
    if (!(error instanceof ActiveConnectionChangedError)) return false;
    const copy = activeConnectionChangedCopy(t);
    const title = options.send ? copy.sendTitle : options.recalculate ? copy.recalculateTitle : copy.title;
    toast.error(title, {
        description: copy.pageDescription,
        ...(options.id !== undefined ? { id: options.id } : {}),
    });
    return true;
}

/**
 * Said on a settings card when the server refused one of its writes
 * (`ActiveConnectionChangedError`): another property is open now than the one
 * the card was loaded for. Nothing was saved, and the page shows the open
 * property only once it is loaded again, so the notice offers the reload. It
 * stays until one of the card's writes goes through, which it can once the
 * other tab opens the card's property again. A tab that finds another property
 * open without writing anything says so for the whole page instead
 * (`PropertyOpenedElsewhereNotice`).
 */
export function ActiveConnectionChangedNotice() {
    const { t } = useLanguage();
    const descriptionId = useId();
    const copy = activeConnectionChangedCopy(t);

    return (
        <Alert variant="destructive">
            <AlertTriangle aria-hidden="true" />
            <AlertTitle>{copy.title}</AlertTitle>
            <AlertDescription>
                {/* Sized on the line itself: the base layer sizes a bare <p>
                    at 18px whatever its container says. */}
                <p id={descriptionId} className="text-sm">
                    {copy.description}
                </p>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => window.location.reload()}
                    aria-describedby={descriptionId}
                    className="mt-1 h-10 sm:h-8"
                >
                    {copy.reload}
                </Button>
            </AlertDescription>
        </Alert>
    );
}
